import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AGV_CONFIGURATOR_IMAGE,
  AGV_OVERLAY_ASPECT_RATIO,
  AGV_OVERLAY_MAX_WIDTH_PX,
  fallbackChipPercent,
} from "../shared/numberRangeChipLayout";

export type AgvCanvasChip = {
  id: number;
  kind: "base" | "sub";
  digit: number;
  label: string;
  chip_top?: number | null;
  chip_left?: number | null;
  /** Shown on hover (e.g. description from API). */
  tooltip?: string | null;
};

type DragState = {
  kind: "base" | "sub";
  id: number;
  startX: number;
  startY: number;
  startTop: number;
  startLeft: number;
};

type Props = {
  chips: AgvCanvasChip[];
  layoutEditMode: boolean;
  onCommitPercent: (kind: "base" | "sub", id: number, chip_top: number, chip_left: number) => void;
};

function chipKey(kind: "base" | "sub", id: number) {
  return `${kind}-${id}`;
}

function effectivePercent(
  c: AgvCanvasChip,
  index: number,
  total: number,
  live: Record<string, { top: number; left: number }>
): { top: number; left: number } {
  const k = chipKey(c.kind, c.id);
  if (live[k]) return live[k];
  if (c.chip_top != null && c.chip_left != null && Number.isFinite(c.chip_top) && Number.isFinite(c.chip_left)) {
    return { top: c.chip_top, left: c.chip_left };
  }
  const fb = fallbackChipPercent(index, total);
  return { top: fb.chip_top, left: fb.chip_left };
}

export default function AdminAgvChipCanvas({ chips, layoutEditMode, onCommitPercent }: Props) {
  const theme = useTheme();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [live, setLive] = useState<Record<string, { top: number; left: number }>>({});
  const commitRef = useRef(onCommitPercent);
  commitRef.current = onCommitPercent;

  const sorted = useMemo(() => [...chips].sort((a, b) => a.digit - b.digit || a.id - b.id), [chips]);
  const n = sorted.length;

  const chipsStableKey = useMemo(
    () =>
      chips
        .map(
          (c) =>
            `${c.kind}:${c.id}:${c.chip_top ?? ""}:${c.chip_left ?? ""}:${(c.tooltip ?? "").slice(0, 120)}`
        )
        .sort()
        .join("|"),
    [chips]
  );

  useEffect(() => {
    setLive({});
  }, [chipsStableKey]);

  useEffect(() => {
    if (!drag || !wrapRef.current) return;
    const wrap = wrapRef.current;
    const onMove = (e: MouseEvent) => {
      const r = wrap.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return;
      const dLeft = ((e.clientX - drag.startX) / r.width) * 100;
      const dTop = ((e.clientY - drag.startY) / r.height) * 100;
      let left = drag.startLeft + dLeft;
      let top = drag.startTop + dTop;
      left = Math.min(100, Math.max(0, left));
      top = Math.min(100, Math.max(0, top));
      setLive((prev) => ({
        ...prev,
        [chipKey(drag.kind, drag.id)]: { top, left },
      }));
    };
    const onUp = () => {
      setLive((prev) => {
        const k = chipKey(drag.kind, drag.id);
        const pos = prev[k];
        if (
          pos &&
          (Math.abs(pos.top - drag.startTop) > 0.2 || Math.abs(pos.left - drag.startLeft) > 0.2)
        ) {
          commitRef.current(drag.kind, drag.id, pos.top, pos.left);
        }
        return prev;
      });
      setDrag(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const onChipMouseDown = useCallback(
    (e: React.MouseEvent, c: AgvCanvasChip, index: number) => {
      if (!layoutEditMode) return;
      e.preventDefault();
      const eff = effectivePercent(c, index, n, live);
      setDrag({
        kind: c.kind,
        id: c.id,
        startX: e.clientX,
        startY: e.clientY,
        startTop: eff.top,
        startLeft: eff.left,
      });
    },
    [layoutEditMode, live, n]
  );

  return (
    <Box
      ref={wrapRef}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: AGV_OVERLAY_MAX_WIDTH_PX,
        mx: "auto",
        aspectRatio: AGV_OVERLAY_ASPECT_RATIO,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        userSelect: layoutEditMode ? "none" : undefined,
      }}
    >
      <Box
        component="img"
        src={AGV_CONFIGURATOR_IMAGE}
        alt="AGV"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          pointerEvents: "none",
        }}
      />
      {sorted.map((c, index) => {
        const eff = effectivePercent(c, index, n, live);
        const tip = (c.tooltip ?? "").trim();
        return (
          <Tooltip
            key={chipKey(c.kind, c.id)}
            title={tip}
            placement="top"
            enterDelay={350}
            disableHoverListener={!tip}
            slotProps={{
              tooltip: { sx: { maxWidth: 380, whiteSpace: "pre-wrap" } },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: `${eff.top}%`,
                left: `${eff.left}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <Chip
                label={c.label}
                color="warning"
                sx={{
                  cursor: layoutEditMode ? (drag?.id === c.id ? "grabbing" : "grab") : "default",
                }}
                onMouseDown={(e) => onChipMouseDown(e, c, index)}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
